import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Helmet } from "react-helmet";

import './accomodation.css';
import useScrollToTop from './ScrollToTop';

const Accomodation = () => {
  useScrollToTop();
  const { t } = useTranslation('accomodations');

  return (
    <div className="accommodation-wrapper">

      {/**SEO AND AI */}

      <Helmet>
  <title>Kenya Safari Accommodations | Lodges, Tented Camps & Hotels</title>
  <meta name="description" content="Explore our curated list of Kenya safari accommodations — from budget camps to luxury lodges across Maasai Mara, Amboseli, Naivasha and beyond." />
  <meta name="keywords" content="Kenya safari lodges, budget accommodation Kenya, luxury safari Kenya, tented camps Kenya, safari hotels, Maasai Mara lodges, Amboseli camps" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://oleninisafaris.com/accomodations" />

  {/* Open Graph / Facebook */}
  <meta property="og:title" content="Kenya Safari Accommodations | Olenini Safaris" />
  <meta property="og:description" content="Stay in the best safari lodges, luxury tented camps, and eco-accommodations across Kenya. Budget to premium options available." />
  <meta property="og:image" content="https://oleninisafaris.com/images/cliff-kenya-banner7.jpg" />
  <meta property="og:url" content="https://oleninisafaris.com/accomodations" />
  <meta property="og:type" content="website" />

  {/* Twitter */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Kenya Safari Accommodations | Olenini Safaris" />
  <meta name="twitter:description" content="Browse the top safari accommodations in Kenya. We offer tented camps, safari lodges, and luxury hotels for all budgets." />
  <meta name="twitter:image" content="https://oleninisafaris.com/images/cliff-kenya-banner7.jpg" />

  {/**LODGING SVHEME */}
  <script type="application/ld+json">
{`
{
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "Kenya Safari Lodges and Camps",
  "itemListElement": [
    {
      "@type": "LodgingBusiness",
      "name": "Sentrim Mara Camp",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Maasai Mara",
        "addressCountry": "Kenya"
      },
      "priceRange": "$",
      "url": "https://oleninisafaris.com/accomodations"
    },
    {
      "@type": "LodgingBusiness",
      "name": "Amboseli Serena Safari Lodge",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Amboseli",
        "addressCountry": "Kenya"
      },
      "priceRange": "$$$",
      "url": "https://oleninisafaris.com/accomodations"
    },
    {
      "@type": "LodgingBusiness",
      "name": "Lake Naivasha Westbeach Camp",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Lake Naivasha",
        "addressCountry": "Kenya"
      },
      "priceRange": "$$",
      "url": "https://oleninisafaris.com/accomodations"
    }
  ]
}
`}
</script>

</Helmet>


      {/* HERO */}
      <section className="accommodation-hero-section">
        <img
          src="/images/cliff-kenya-banner7.jpg"
          alt="Mara National Reserve"
          className="accommodation-hero-image"
        />
      </section>

      {/* INTRO */}
      <div className="accommodation-intro-card">
        <h3 className="accommodation-intro-title">{t('intro.title')}</h3>
        <div className="accommodation-location-list">
          <a href="#accomodationmara" className="accommodation-location-link">{t('intro.locations.0')}</a>
          <span className="accommodation-divider"></span>
          <a href="#accomodationnaivasha" className="accommodation-location-link">{t('intro.locations.1')}</a>
          <span className="accommodation-divider"></span>
          <a href="#accomodationamboseli" className="accommodation-location-link">{t('intro.locations.2')}</a>
          <span className="accommodation-divider"></span>
          <a href="#accomodationnakuru" className="accommodation-location-link">{t('intro.locations.3')}</a>
          <span className="accommodation-divider"></span>
          <a href="#narokhomestay" className="accommodation-location-link">{t('intro.locations.4')}</a>
        </div>
      </div>

      {/* MARA ACCOMMODATIONS */}
      <section id="accomodationmara" className="accommodation-region-section">
        <div className="accommodation-grid-container">
          {/* OLDARPOI */}
          <div className="accommodation-property-card">
            <div className="accommodation-property-badge">{t('intro.locations.0')}</div>
            <img
              src="/images/oldarpoiA.jpg"
              alt={t('oldarpoi.title')}
              loading="lazy"
              className="accommodation-property-image"
            />
            <div className="accommodation-property-details">
              <h4 className="accommodation-property-name">{t('oldarpoi.title')}</h4>
              <p className="accommodation-property-description">
                <Trans
                  i18nKey="oldarpoi.description"
                  ns="accomodations"
                  components={{ strong: <strong /> }}
                />
              </p>
            </div>
          </div>

          {/* OSEKI */}
          <div className="accommodation-property-card">
            <div className="accommodation-property-badge">{t('osekiLocation')}</div>
            <img
              src="/images/oseki.jpeg"
              alt={t('oseki.title')}
              loading="lazy"
              className="accommodation-property-image"
            />
            <div className="accommodation-property-details">
              <h4 className="accommodation-property-name">{t('oseki.title')}</h4>
              <p className="accommodation-property-description">{t('oseki.description')}</p>
            </div>
          </div>
        </div>
        <br />
        <hr />
        <br />
      </section>

      {/* NAIVASHA */}
      <section id="accomodationnaivasha" className="accommodation-region-section">
        <div className="accommodation-grid-container">
          {/* WEST BEACH */}
          <div className="accommodation-property-card">
            <div className="accommodation-property-badge">
              {t('lakeNaivashaWestBeach.label')}
            </div>
            <img
              src="/images/westbeach.jpg"
              alt="Naivasha West Beach Camp"
              loading="lazy"
              className="accommodation-property-image"
            />
            <div className="accommodation-property-details">
              <h4 className="accommodation-property-name">{t('lakeNaivashaWestBeach.title')}</h4>
              <p className="accommodation-property-description">{t('lakeNaivashaWestBeach.description')}</p>
            </div>
          </div>

          {/* PANORAMA */}
          <div className="accommodation-property-card">
            <div className="accommodation-property-badge">
              {t('panoramaParkHotelNaivasha.label')}
            </div>
            <img
              src="/images/panorama.jpg"
              alt="Panorama Park Hotel Naivasha"
              loading="lazy"
              className="accommodation-property-image"
            />
            <div className="accommodation-property-details">
              <h4 className="accommodation-property-name">{t('panoramaParkHotelNaivasha.title')}</h4>
              <p className="accommodation-property-description">{t('panoramaParkHotelNaivasha.description')}</p>
            </div>
          </div>
        </div>
        <br />
        <hr />
        <br />
      </section>

      {/* NAROK HOMESTAY */}
      <section id="narokhomestay" className="accommodation-special-section">
        <div className="accommodation-featured-card">
          <div className="accommodation-featured-image-wrapper">
            <div className="accommodation-featured-badge">Serian Narok Homestay</div>
            <img
              src="/images/narokhomestay.jpeg"
              alt="Serian Narok Homestay"
              className="accommodation-featured-image"
            />
          </div>
          <div className="accommodation-featured-content">
            <h3 className="accommodation-featured-title">{t('serianNarokHomestay.title')}</h3>
            <p className="accommodation-featured-description">{t('serianNarokHomestay.description')}</p>
          </div>
        </div>
        <br />
        <hr />
        <br />
      </section>

      {/* AMBOSELI */}
      <section id="accomodationamboseli" className="accommodation-special-section">
        <div className="accommodation-featured-card">
          <div className="accommodation-featured-image-wrapper">
            <div className="accommodation-featured-badge">
              Serena Amboseli Safari Lodge
            </div>
            <img
              src="/images/amboseliserena-40.jpg"
              alt="Serena Amboseli Safari Lodge"
              className="accommodation-featured-image"
            />
          </div>
          <div className="accommodation-featured-content">
            <h3 className="accommodation-featured-title">{t('serenaAmboseliSafariLodge.title')}</h3>
            <p className="accommodation-featured-description">{t('serenaAmboseliSafariLodge.description')}</p>
          </div>
        </div>
        <br />
        <hr />
        <br />
      </section>

      {/* NAKURU */}
      <section id="accomodationnakuru" className="accommodation-special-section">
        <div className="accommodation-featured-card">
          <div className="accommodation-featured-image-wrapper">
            <div className="accommodation-featured-badge">Midland Hotel Nakuru</div>
            <img 
              src="/images/midlandhotel.jpg" 
              alt="Midland Hotel" 
              className="accommodation-featured-image"
            />
          </div>
          <div className="accommodation-featured-content">
            <h3 className="accommodation-featured-title">{t('midlandHotel.title')}</h3>
            <p className="accommodation-featured-description">{t('midlandHotel.description')}</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="accommodation-cta-container" style={{ textAlign: 'center', marginTop: '20px' }}>
        <a
          href="/contact"
          className="accommodation-cta-button"
          style={{
            backgroundColor: '#3b2f2f',
            color: '#fffdfb',
            padding: '12px 24px',
            borderRadius: '4px',
            textDecoration: 'none',
            fontWeight: 'bold',
            fontFamily: 'Georgia, serif',
            fontSize: '1rem',
            display: 'inline-block',
            transition: 'background-color 0.3s ease',
            marginBottom: '2.5px',
          }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = '#5a4949')
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = '#3b2f2f')
          }
        >
          {t('startSafari')}
        </a>
      </div>
    </div>
  );
};

export default Accomodation;